// ---------------------------------------------------------------------------
// OpeningHours Renderer (editor key: OpeningHours)
// ---------------------------------------------------------------------------
// Renders a table of business hours. Editor input is a pipe-delimited
// textarea ("Day | Hours" per line; use "Closed" for non-operating days).
// Day labels are matched against a safelist for the `data-day` attribute so
// downstream theme JS and structured-data enrichment can rely on a stable
// vocabulary; arbitrary day labels still render as visible rows but get a
// generic `data-day="custom"` hook. No JS required, table is semantic.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

interface OpeningHoursProps {
  heading?: string;
  hours?: string;
  timezone?: string;
}

interface HoursEntry {
  day: string;
  hours: string;
  dayKey: string;
}

const DAY_KEYS: Record<string, string> = {
  mon: "monday",
  monday: "monday",
  tue: "tuesday",
  tues: "tuesday",
  tuesday: "tuesday",
  wed: "wednesday",
  weds: "wednesday",
  wednesday: "wednesday",
  thu: "thursday",
  thur: "thursday",
  thurs: "thursday",
  thursday: "thursday",
  fri: "friday",
  friday: "friday",
  sat: "saturday",
  saturday: "saturday",
  sun: "sunday",
  sunday: "sunday",
};

function lookupDayKey(day: string): string {
  return DAY_KEYS[day.toLowerCase()] ?? "custom";
}

function parseHours(value: unknown): HoursEntry[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [dayPart, hoursPart] = line.split("|");
      const day = (dayPart ?? "").trim();
      const hours = (hoursPart ?? "").trim() || "Closed";
      return { day, hours, dayKey: lookupDayKey(day) };
    })
    .filter((entry) => entry.day.length > 0);
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as OpeningHoursProps;

  const rows = parseHours(p.hours);
  if (rows.length === 0) {
    return "<!-- opening-hours missing rows -->";
  }

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const timezone = typeof p.timezone === "string" ? p.timezone.trim() : "";

  const headingHtml = heading
    ? `\n  <h2 class="ease-opening-hours__heading">${text(heading)}</h2>`
    : "";
  const tzHtml = timezone
    ? `\n  <p class="ease-opening-hours__timezone">${text(timezone)}</p>`
    : "";

  const trs = rows
    .map(
      (entry) =>
        `      <tr class="ease-opening-hours__row" data-day="${attr(entry.dayKey)}">\n        <th scope="row" class="ease-opening-hours__day">${text(entry.day)}</th>\n        <td class="ease-opening-hours__hours">${text(entry.hours)}</td>\n      </tr>`,
    )
    .join("\n");

  return `<section class="ease-section ease-opening-hours">${headingHtml}${tzHtml}
  <table class="ease-opening-hours__table">
    <tbody>
${trs}
    </tbody>
  </table>
</section>`;
}

registerRenderer("opening-hours", { render });
